import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type FiltersState = {
  skillIds: number[];
  jurisdictionIds: number[];
  allCountry: boolean;
  page: number;
  name: string;
  featured?: boolean;
};

type UseProfesionalesFiltersArgs = {
  onApply: (filters: FiltersState) => void;
  initialPage?: number;
};

const useProfesionalesFilters = ({ onApply, initialPage = 1 }: UseProfesionalesFiltersArgs) => {
  const [skillIds, setSkillIds] = useState<number[]>([]);
  const [jurisdictionIds, setJurisdictionIds] = useState<number[]>([]);
  const [allCountry, setAllCountry] = useState(false);
  const [page, setPage] = useState(initialPage);
  const [name, setNameState] = useState("");
  const [featured, setFeatured] = useState(false);
  const shouldSyncRef = useRef(false);

  const toggleSkill = useCallback(
    (id: number) => {
      shouldSyncRef.current = true;
      const numId = Number(id);
      setSkillIds((prev) => {
        const hasId = prev.some((p) => Number(p) === numId);
        return hasId ? prev.filter((p) => Number(p) !== numId).map(Number) : [numId];
      });
      setPage(1);
    },
    []
  );

  const toggleJurisdiction = useCallback(
    (id: number) => {
      shouldSyncRef.current = true;
      const numId = Number(id);
      setJurisdictionIds((prev) => {
        const hasId = prev.some((p) => Number(p) === numId);
        return hasId ? prev.filter((p) => Number(p) !== numId).map(Number) : [numId];
      });
      setAllCountry(false);
      setPage(1);
    },
    []
  );

  const toggleAllCountry = useCallback(() => {
    shouldSyncRef.current = true;
    setAllCountry((prev) => {
      const nextAllCountry = !prev;
      const nextJurisdictions = nextAllCountry ? [] : jurisdictionIds;
      return nextAllCountry;
    });
    setJurisdictionIds([]);
    setPage(1);
  }, []);

  const toggleFeatured = useCallback(() => {
    shouldSyncRef.current = true;
    setFeatured(false);
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    shouldSyncRef.current = true;
    setSkillIds([]);
    setJurisdictionIds([]);
    setAllCountry(false);
    setNameState("");
    setFeatured(false);
    setPage(1);
  }, []);

  const setName = useCallback((term: string) => {
    shouldSyncRef.current = true;
    setNameState(term);
    setPage(1);
  }, []);

  const applyFilters = useCallback(() => {
    const filters: FiltersState = {
      skillIds,
      jurisdictionIds,
      allCountry,
      page,
      name,
      featured,
    };
    onApply(filters);
  }, [allCountry, featured, jurisdictionIds, onApply, page, skillIds, name]);

  const setPageAndApply = useCallback(
    (nextPage: number) => {
      shouldSyncRef.current = true;
      setPage(nextPage);
    },
    [allCountry, jurisdictionIds, skillIds]
  );

  const filtersCount = useMemo(() => {
    let count = 0;
    if (skillIds.length > 0) count += 1;
    if (jurisdictionIds.length > 0) count += 1;
    if (allCountry) count += 1;
    if (featured) count += 1;
    if (name.trim().length > 0) count += 1;
    return count;
  }, [allCountry, featured, jurisdictionIds.length, skillIds.length, name]);

  const initialize = useCallback(
    (initial: FiltersState) => {
      shouldSyncRef.current = true;
      const toNumbers = (arr: unknown[]) =>
        (arr ?? []).map((x) => Number(x)).filter((n) => !Number.isNaN(n));
      setSkillIds(toNumbers(initial.skillIds ?? []));
      setJurisdictionIds(toNumbers(initial.jurisdictionIds ?? []));
      setAllCountry(initial.allCountry);
      setNameState(initial.name ?? "");
      setFeatured(initial.featured ?? false);
      setPage(initial.page);
    },
    []
  );

  useEffect(() => {
    if (!shouldSyncRef.current) {
      return;
    }
    const filters: FiltersState = {
      skillIds,
      jurisdictionIds,
      allCountry,
      page,
      name,
      featured,
    };
    onApply(filters);
  }, [skillIds, jurisdictionIds, allCountry, featured, page, name, onApply]);

  return {
    state: {
      skillIds,
      jurisdictionIds,
      allCountry,
      page,
      name,
      featured,
    },
    actions: {
      toggleSkill,
      toggleJurisdiction,
      toggleAllCountry,
      toggleFeatured,
      clearFilters,
      applyFilters,
      setPage: setPageAndApply,
      setName,
      initialize,
    },
    meta: {
      filtersCount,
    },
  };
};

export default useProfesionalesFilters;

